import React from "react";
import { Select } from "antd";
import type { SelectProps } from "antd";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { PaginationResponse } from "~/services";

const { Option } = Select;

// Generic type for options
interface Option {
  [key: string]: any; // Flexible for various option shapes
}

// Props for InfiniteScrollSelect
interface InfiniteScrollSelectProps<T extends Option> extends SelectProps {
  getOptionValue: (item: T) => string | number;
  getOptionLabel: (item: T) => React.ReactNode;
  allOptions?: {
    value: string | number;
    label: React.ReactNode;
  };
  queryResult?: UseInfiniteQueryResult<
    InfiniteData<PaginationResponse<T>, unknown>,
    Error
  >; // Query result as a property instead of extension
}

export const InfiniteScrollSelect = <T extends Option>({
  allOptions,
  getOptionValue,
  getOptionLabel,
  queryResult,
  ...selectProps
}: InfiniteScrollSelectProps<T>) => {
  const options = queryResult?.data?.pages.flatMap((page) => page.items) ?? [];

  // Handle scroll to bottom
  const handlePopupScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const target = e.target as HTMLDivElement;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
      queryResult?.hasNextPage &&
      !queryResult?.isFetchingNextPage
    ) {
      queryResult?.fetchNextPage();
    }
  };

  return (
    <Select
      placeholder="Select an option"
      onPopupScroll={handlePopupScroll}
      showSearch
      filterOption={(input, option) =>
        option?.children
          ?.toString()
          .toLowerCase()
          .includes(input.toLowerCase()) ?? false
      }
      {...selectProps}
      loading={queryResult?.isFetching || queryResult?.isLoading || false}
    >
      {allOptions && (
        <Option value={allOptions.value}>{allOptions.label}</Option>
      )}
      {options.map((item) => (
        <Option key={getOptionValue(item)} value={getOptionValue(item)}>
          {getOptionLabel(item)}
        </Option>
      ))}
      {queryResult?.isFetchingNextPage && (
        <Option disabled>Loading more...</Option>
      )}
    </Select>
  );
};
